import { MagnifyingGlassIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useGetAnnouncementRecipients } from '@/api/communication/get-announcement-recipients'
import { ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY } from '@/api/communication/get-announcement-recipients/get-announcement-recipients'
import { usePersonColumnSettings } from '@/components/business/person-column'
import { TableGroup } from '@/components/layout/table-group'
import { StatusTabs } from '@/components/ui/status-tabs'
import { TextInput } from '@/components/ui/text-input'
import { DEFAULT_TZ } from '@/constants'
import { useFilters } from '@/hooks/use-filters'
import { useSearchParams } from '@/hooks/use-search-params'
import {
  getAnnouncementRecipientCounts,
  getAnnouncementRecipientsTotal,
} from '@/pages/communication/announcement-details/announcement-details.counts'
import { getRecipientStatusParam } from '@/pages/communication/announcement-details/announcement-details.page.utils'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  EmailStatusFilter,
  emailRecipientsTableFiltersSchema,
  emailRecipientsTableInitialFilters,
  getEmailStatusOptions,
  getEmailTableColumns,
} from './email-recipients-table.constants'

export const EmailRecipientsTable = () => {
  const { t } = useTranslation(['communication'])
  const params = useParams()
  const activeBuilding = useSelector(selectActiveBuilding)

  const announcementId = Number(params.id)
  const buildingId = activeBuilding?.id
  const timezone = activeBuilding?.timezone || DEFAULT_TZ
  const searchParams = useSearchParams({
    initialState: emailRecipientsTableInitialFilters,
    schema: emailRecipientsTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: emailRecipientsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { limit, skip, sortBy, sortOrder, status } = tableFilters.filters
  const search = tableFilters.filters.search?.trim()

  const statusParam = getRecipientStatusParam(status)

  const { data, isFetching, error } = useGetAnnouncementRecipients({
    buildingId,
    id: announcementId,
    limit,
    skip,
    ...(sortBy && { sortBy }),
    ...(sortOrder && { sortOrder }),
    ...(search && { query: search }),
    ...(statusParam && { status: statusParam }),
  })

  const countMap = getAnnouncementRecipientCounts(data)

  const emailStatusOptions = getEmailStatusOptions(t).map((option) => ({
    ...option,
    count: countMap[option.value],
  }))

  const residentColumnSettings = usePersonColumnSettings({
    column: 'resident',
    dashboard: 'Announcements',
    fieldType: 'resident',
    rowsQueryKey: [ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY],
  })
  const columns = getEmailTableColumns(t, timezone, residentColumnSettings)

  return (
    <TableGroup
      id="announcement-recipients-email"
      columns={columns}
      data={data?.recipients || []}
      emptyNoFilters={{
        description: t(
          'communication:announcementDetails.recipientsTable.email.empty.noFilters.description',
        ),
        title: t('communication:announcementDetails.recipientsTable.email.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t(
          'communication:announcementDetails.recipientsTable.empty.withFilters.description',
        ),
        title: t('communication:announcementDetails.recipientsTable.empty.withFilters.title'),
      }}
      error={error}
      enableColumnVisibility={false}
      getRowKey={(recipient) => recipient.id}
      loading={isFetching}
      navigation={{
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            name="search"
            placeholder={t('communication:announcementDetails.recipientsTable.search.placeholder')}
            size="small"
            value={tableFilters.filters.search || ''}
            onChange={(event) => tableFilters.setFilter('search', event.target.value)}
          />
        ),
        statusTabs: (
          <StatusTabs<EmailStatusFilter>
            options={emailStatusOptions}
            value={status || emailRecipientsTableInitialFilters.status || null}
            onChange={(value) => tableFilters.setFilter('status', value)}
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total: getAnnouncementRecipientsTotal(countMap, status),
      }}
      sortBy={tableFilters.getFilter('sortBy')}
      sortOrder={tableFilters.getFilter('sortOrder')}
      tableFilters={tableFilters}
    />
  )
}
